import type { ReactNode } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import ArticleHero from '../../../components/news/ArticleHero';
import PullQuote from '../../../components/news/PullQuote';
import KeyPointsBox from '../../../components/news/KeyPointsBox';
import CommentBox from '../../../components/news/CommentBox';
import SourceCard from '../../../components/news/SourceCard';
import RelatedCards from '../../../components/news/RelatedCards';

/* ---- 記事内インライン部品（報道メディア型・帯は使わない） ---- */

function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        出典: {media}（<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>）
      </figcaption>
    </figure>
  );
}

function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function SwissLuxuryTravelSkiButlerPage() {
  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'ニュース・お知らせ', href: '/news' },
    { label: 'スイス超豪華旅行ブーム：プライベートスキーバトラー' }
  ];

  const article = {
    category: '超富裕層市場・専門サービス',
    date: '2025年3月3日',
    title: 'スイス超豪華旅行ブーム：プライベートスキーバトラー',
    subtitle: '超富裕層市場における執事サービスの経済的インパクト',
    heroImage: '/images/generated/jba-fs-124-news_swiss-luxury-travel-ski-butler.webp',
    lead: 'スイスアルプスで、プライベートスキーバトラー、住み込みシェフ、希少な葉巻コレクションなど、超豪華旅行サービスのブームが到来しています。2024年にスイスのホテルは50年以上で最高の4,280万泊を記録し、5つ星ホテルのゲストが観光収入全体の25-30%を占める経済的インパクトを示しています。',
    comment: 'スイスアルプスにおけるプライベートスキーバトラーという高度に専門化したサービスの出現は、執事サービスの進化と多様化を示す重要な事例として、弊協会としても深く注目しております。特に、超富裕層市場における執事サービスが観光収入の25から30%という経済的インパクトを持つことは、パーソナライゼーションの極致としての専属バトラーサービスの価値を明確に示しています。\n\n富裕層旅行体験における執事の不可欠性が、このような具体的な数値として表れていることは、執事という職業の経済的重要性を証明するものです。\n\n日本の高級リゾート市場においても、このような専門化された執事サービスの導入可能性があり、伝統的ホスピタリティと現代的ラグジュアリーの融合という観点から、私どもとしても、季節や地域特性に応じた専門執事サービスの開発と、超富裕層のニーズに対応できる高度な専門性を持つ執事の育成に積極的に取り組んでまいります。',
    related: [
      { to: '/news/raffles-sentosa-butler-package', image: '/images/generated/jba-fs-130-news_swiss-luxury-travel-ski-butler-v4.webp', aspect: 'aspect-[3/4]', alt: 'Raffles Sentosa「The Butler Did It」パッケージの関連記事', title: 'Raffles Sentosa「The Butler Did It」パッケージ', desc: '1泊約55万円の究極のビスポークラグジュアリー体験' },
      { to: '/news/ai-hospitality-revolution', image: '/images/generated/jba-fs-131-news_swiss-luxury-travel-ski-butler-v4.webp', aspect: 'aspect-[4/3]', alt: 'AIホスピタリティ革命：5つの変革の関連記事', title: 'AIホスピタリティ革命：5つの変革', desc: '人工知能が切り開く執事サービスの未来' }
    ]
  };

  const sources = [
    { label: '出典元', value: 'Euronews', link: false },
    { label: '記事URL', value: 'https://www.euronews.com/travel/2025/03/03/private-ski-butlers-live-in-chefs-and-rare-cigars-inside-switzerlands-ultra-luxury-travel-', link: true },
    { label: '希少葉巻（The Chedi Andermatt）', value: 'https://www.thechediandermatt.com/dining/the-cigar-library', link: true },
    { label: 'シャレー相場', value: 'https://www.firefly-collection.com/luxury-ski-chalets/luxury-catered-ski-chalets/', link: true },
    { label: 'プライベートシェフ料金', value: 'https://adam-brunet.com/en/chalet-chef', link: true },
    { label: 'アクセス日', value: '2026年7月', link: false }
  ];

  const toc = [
    { id: 's-market', label: '市場概況と記録的成長' },
    { id: 's-vip', label: 'プライベートスキーバトラーとVIPサービス' },
    { id: 's-chalet', label: 'プライベートシャレーの相場' },
    { id: 's-fivestar', label: '5つ星宿泊施設の贅沢な標準装備' },
    { id: 's-chef', label: '住み込みシェフの料金水準' },
    { id: 's-econ', label: 'ラグジュアリーツーリズムの経済効果' },
    { id: 's-diff', label: 'スイスの差別化要因と競合比較' }
  ];

  return (
    <Layout>
      <ArticleHero eyebrow="News / Luxury Market" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="この記事のポイント"
              points={[
                '2024年、スイスのホテルは4,280万泊を記録し、50年以上で最高を更新。牽引役は米国からの記録的な訪問者を含むラグジュアリー旅行者',
                '5つ星ホテルは全宿泊数の約8%に過ぎないが、そのゲストがスイス観光収入全体の25〜30%を占める',
                'St. Moritz、Zermatt、Gstaadなどのアルプス各地で、スキーブーツの予熱やヘリスキーなど高度にパーソナライズされたスキーバトラーサービスが提供されている'
              ]}
            />

            {/* 01: 市場概況（既存本文・一字も削らず） */}
            <SectionHeading id="s-market" en="Market">市場概況と記録的成長</SectionHeading>
            <div className="space-y-6">
              <p className={P}>世界の富裕層のビジネスが好調な中、スイスの観光業も急成長を遂げています。2024年、スイスのホテルは4,280万泊の宿泊数を記録し、50年以上で最高を更新しました。この成長の主な牽引役は、米国からの記録的な訪問者数を誇るラグジュアリー旅行者です。</p>
              <p className={P}>World Economic Forumによると、世界の億万長者は2024年に集合的富を1兆9,400億ユーロ増やしており、この富の増加がスイスの超高級旅行市場の拡大に直接的に寄与しています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/swiss-luxury-travel-ski-butler-euronews.jpg"
              alt="Euronewsに掲載されたスイスの超高級旅行トレンドとプライベートスキーバトラーを報じる記事のスクリーンショット"
              media="Euronews"
              url="https://www.euronews.com/travel/2025/03/03/private-ski-butlers-live-in-chefs-and-rare-cigars-inside-switzerlands-ultra-luxury-travel-"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="5つ星ホテルは全宿泊の約8%だが、そのゲストが観光収入の25〜30%を占める。" cite="Euronews / Switzerland Tourism" />
            </div>

            {/* 02: スキーバトラー（既存本文・一字も削らず） */}
            <SectionHeading id="s-vip" en="Ski Butlers">プライベートスキーバトラーとVIPサービス</SectionHeading>
            <div className="space-y-6">
              <p className={P}>パンデミック後、スイスは「少数の人々のためにカスタマイズされた」パーソナライズされた高級体験にさらに力を入れています。St. Moritz、Zermatt、Gstaadなどのアルプスの目的地のホテルは、革新的なサービスでレベルを引き上げています。</p>
              <p className={P}>特に注目されるのは、プライベートスキーバトラーによるスキーブーツを事前に温めるサービス、ヘリスキーツアー、ゲスト用Harley-Davidsonモーターサイクルなど、従来の概念を超えた高度にパーソナライズされたサービスです。</p>
            </div>

            {/* 増補: シャレーの相場 */}
            <SectionHeading id="s-chalet" en="Chalet Rentals">プライベートシャレーの相場</SectionHeading>
            <div className="space-y-6">
              <p className={P}>スイスの高級スキーシャレーは、貸別荘各社の掲載例では週額<Stat>15万</Stat>〜<Stat>35万ユーロ</Stat>で提供されています。VerbierやZermattの物件では、ゲレンデへ直接出入りできるスキーイン/スキーアウトのアクセスや、プライベートシェフのサービスが標準的に含まれます。</p>
              <p className={P}>こうしたシャレーは、スキーバトラーや住み込みスタッフを備え、一棟を丸ごと貸し切る滞在形態として、超富裕層に選ばれています。</p>
            </div>

            {/* 03: 5つ星標準装備（既存本文・一字も削らず） */}
            <SectionHeading id="s-fivestar" en="Five-Star">5つ星宿泊施設の贅沢な標準装備</SectionHeading>
            <div className="space-y-6">
              <p className={P}>スイスの5つ星宿泊施設には、住み込みシェフ、一生に一度の体験をキュレートするコンシェルジュサービスが標準装備として提供されています。</p>
              <p className={P}>特に印象的なのは、The Chediが所有する数千本の希少葉巻コレクションで、最高8,600ユーロ（約150万円）の葉巻も含まれています。このような極めて専門的で高額なサービスが、超富裕層の期待に応える水準として提供されています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/swiss-luxury-travel-ski-butler-thechedi.jpg"
              alt="The Chedi Andermatt公式サイトのシガーライブラリー紹介ページのスクリーンショット。希少葉巻コレクションが掲載されている"
              media="The Chedi Andermatt 公式サイト"
              url="https://www.thechediandermatt.com/dining/the-cigar-library"
            />

            {/* 増補: 住み込みシェフの料金水準 */}
            <SectionHeading id="s-chef" en="Live-in Chefs">住み込みシェフの料金水準</SectionHeading>
            <div className="space-y-6">
              <p className={P}>アルプス地方でシャレーに滞在するゲスト向けのプライベートシェフは、掲載料金で1日<Stat>400ユーロ</Stat>前後から利用できます。朝食と夕食を含むパッケージでは、1週間あたり<Stat>2,800ユーロ</Stat>前後が目安とされています。</p>
              <p className={P}>こうした住み込み・通いのシェフサービスは、スキーバトラーと並んで、シャレー滞在のパーソナライズを支える中核的な存在となっています。</p>
            </div>

            {/* 04: 経済効果（既存本文・一字も削らず） */}
            <SectionHeading id="s-econ" en="Economic Impact">ラグジュアリーツーリズムの経済効果</SectionHeading>
            <div className="space-y-6">
              <p className={P}>スイスにとって、富裕層訪問者戦略は大きな配当を支払っています。2024年の外国人宿泊数は5.1%増の2,200万泊を記録し、米国旅行者が約4分の1を占めています。中国・インド訪問者はそれぞれ2桁成長を達成しています。</p>
              <p className={P}>最も注目すべきは、5つ星ホテルが全宿泊数の約8%に過ぎないにもかかわらず、そこに滞在するゲストがスイスの観光収入全体の25から30%を占めているという事実です。Switzerland TourismのMarkus Berger氏は「数字が物語っています。高い経済的重要性が、ラグジュアリーゲストへのコミットメントを正当化します」とコメントしています。</p>
            </div>

            {/* 05: 差別化要因（既存本文・一字も削らず） */}
            <SectionHeading id="s-diff" en="Differentiation">スイスの差別化要因と競合比較</SectionHeading>
            <div className="space-y-6">
              <p className={P}>スイスのラグジュアリーツーリズムブームは孤立していません。Tenerifeは米国ラグジュアリー旅行者向けに独占的な休暇地として再配置し、Maldives、Austria、Greeceはカスタムイティナリー、プライベートサンクチュアリ、スタンドアロンのウェルネスコンプレックスを提供しています。</p>
              <p className={P}>しかし、DubaiやMaldivesが贅沢を約束する一方、スイスは遺産に根ざした控えめなラグジュアリー、ウェルネス、アウトドアアドベンチャー、世界クラスの、しばしば「自家製」のサービスという独特の組み合わせを提供しています。この組み合わせは、世界で最も裕福な旅行者にとって勝利の方程式であり続ける可能性があります。</p>
            </div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="目次" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">報道</dt><dd className="text-[#333] mt-0.5">2025年3月3日</dd></div>
                  <div><dt className="text-[#8f7a56]">2024年宿泊数</dt><dd className="text-[#333] mt-0.5">4,280万泊（50年ぶり最高）</dd></div>
                  <div><dt className="text-[#8f7a56]">5つ星の収入シェア</dt><dd className="text-[#333] mt-0.5">観光収入の25〜30%</dd></div>
                  <div><dt className="text-[#8f7a56]">カテゴリ</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="全日本執事協会からのコメント" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="出典" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="関連記事" items={article.related} readLabel="詳しく" backHref="/news" backLabel="ニュース・お知らせ一覧へ戻る" />
    </Layout>
  );
}
